import React, { useEffect, useRef, useState } from "react";
import { Box, Paper } from "@mui/material";
import { Clock } from "lucide-react";

const presetTimes = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00",
];

function formatTime(time) {
  const [h, m] = time.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${display}:${m} ${ampm}`;
}

const TimePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <Box sx={{ position: "relative" }} ref={containerRef}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(!open)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "8px",
          whiteSpace: "nowrap",
          borderRadius: "calc(0.75rem - 2px)",
          fontSize: "14px",
          fontWeight: 400,
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          transition: "color 0.2s, background-color 0.2s",
          border: "1px solid hsl(140 8% 88%)",
          backgroundColor: "var(--input-bg, hsl(140 10% 95%))",
          height: "40px",
          px: "16px",
          py: "8px",
          width: "100%",
          textAlign: "left",
          color: value ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
          cursor: "pointer",
          boxSizing: "border-box",
          "&:hover": {
            backgroundColor: "hsl(80 97% 55%)",
            color: "hsl(150 38% 12%)",
          },
          "&:focus-visible": {
            outline: "none",
            boxShadow: "0 0 0 2px hsl(140 10% 95%), 0 0 0 4px hsl(150 38% 17%)",
          },
          "&:disabled": {
            pointerEvents: "none",
            opacity: 0.5,
          },
        }}
      >
        <Clock size={16} style={{ color: "hsl(150, 10%, 46%)", flexShrink: 0 }} />
        {value ? formatTime(value) : "Pick a time\u2026"}
      </Box>
      {open && (
        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            right: 0,
            bottom: "100%",
            zIndex: 50,
            mb: "4px",
            width: "320px",
            maxWidth: "calc(100vw - 2rem)",
            borderRadius: "calc(0.75rem - 2px)",
            border: "1px solid hsl(140 8% 88%)",
            p: "12px",
            color: "hsl(150 30% 12%)",
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
            maxHeight: "288px",
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "4px" }}>
            {presetTimes.map((t) => (
              <Box
                key={t}
                component="button"
                type="button"
                onClick={() => { onChange(t); setOpen(false); }}
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                  px: "8px",
                  py: "6px",
                  borderRadius: "calc(0.75rem - 2px)",
                  transition: "color 0.2s, background-color 0.2s",
                  textAlign: "center",
                  cursor: "pointer",
                  border: "none",
                  backgroundColor: value === t ? "hsl(150 38% 17%)" : "transparent",
                  color: value === t ? "hsl(80 97% 72%)" : "hsl(150 30% 12%)",
                  "&:hover": value === t ? {} : {
                    backgroundColor: "hsl(140 8% 90%)",
                  },
                }}
              >
                {formatTime(t)}
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default TimePicker;
